<script>
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import Modal from 'dashboard/components/Modal.vue';
import EvolutionAPI from 'dashboard/api/evolution';
import InboxesAPI from 'dashboard/api/inboxes';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    NextButton,
    SettingsSection,
    InboxName,
    Modal,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isDisconnecting: false,
      isGeneratingQR: false,
      isRecreating: false,
      isConfiguringWebhook: false,
      isSavingWebhookConfig: false,
      qrCode: null,
      showQrModal: false,
      instanceStatus: null,
      proxySettings: {
        enabled: false,
        host: '',
        port: '',
        username: '',
        password: '',
      },
      instanceSettings: {
        rejectCall: false,
        msgCall: '',
        groupsIgnore: false,
        alwaysOnline: true,
        readMessages: false,
        syncFullHistory: false,
        readStatus: false,
      },
      // Webhook configuration for Evolution GO
      webhookConfig: {
        webhookUrl: '',
        events: {
          MESSAGE: true,
          SEND_MESSAGE: false,
          READ_RECEIPT: false,
          PRESENCE: true,
          HISTORY_SYNC: false,
          CHAT_PRESENCE: false,
          CALL: false,
          CONNECTION: true,
          LABEL: false,
          CONTACT: true,
          GROUP: false,
          NEWSLETTER: false,
          QRCODE: false,
        },
        rabbitmqEnable: 'default',
        websocketEnable: 'default',
        natsEnable: 'default',
      },
      // All available event types
      availableEvents: [
        {
          key: 'MESSAGE',
          label: 'Mensagens',
          description: 'Receber mensagens de texto, mídia, etc.',
        },
        {
          key: 'SEND_MESSAGE',
          label: 'Mensagens Enviadas',
          description: 'Notificar quando mensagens são enviadas',
        },
        {
          key: 'READ_RECEIPT',
          label: 'Confirmação de Leitura',
          description: 'Receber confirmações de leitura',
        },
        {
          key: 'PRESENCE',
          label: 'Presença',
          description: 'Status online/offline dos contatos',
        },
        {
          key: 'HISTORY_SYNC',
          label: 'Sincronização de Histórico',
          description: 'Sincronização de mensagens antigas',
        },
        {
          key: 'CHAT_PRESENCE',
          label: 'Presença no Chat',
          description: 'Digitando, gravando áudio, etc.',
        },
        {
          key: 'CALL',
          label: 'Chamadas',
          description: 'Eventos de chamadas de voz/vídeo',
        },
        {
          key: 'CONNECTION',
          label: 'Conexão',
          description: 'Mudanças no status da conexão',
        },
        {
          key: 'LABEL',
          label: 'Etiquetas',
          description: 'Eventos de etiquetas/tags',
        },
        {
          key: 'CONTACT',
          label: 'Contatos',
          description: 'Eventos de contatos',
        },
        { key: 'GROUP', label: 'Grupos', description: 'Eventos de grupos' },
        {
          key: 'NEWSLETTER',
          label: 'Newsletter/Canais',
          description: 'Eventos de canais/newsletters',
        },
        { key: 'QRCODE', label: 'QR Code', description: 'Eventos de QR Code' },
      ],
      // Messaging options
      messagingOptions: [
        { value: 'default', label: 'Padrão' },
        { value: 'enabled', label: 'Habilitado' },
        { value: 'disabled', label: 'Desabilitado' },
      ],
    };
  },
  computed: {
    canShowQRCode() {
      return !this.isStatusOpen(this.instanceStatus);
    },
    hasValidProxyConfig() {
      if (!this.proxySettings.enabled) return true;
      return this.proxySettings.host && this.proxySettings.port;
    },
    isEvolutionGO() {
      return !!this.inbox.provider_config?.instance_token;
    },
    selectedEventsCount() {
      return Object.values(this.webhookConfig.events).filter(Boolean).length;
    },
    defaultWebhookUrl() {
      const phoneNumber =
        this.inbox.phone_number || this.inbox.provider_config?.phone_number;
      if (!phoneNumber) return '';
      return `${window.location.origin}/webhooks/whatsapp/${phoneNumber.replace(/\+/g, '')}`;
    },
  },
  watch: {
    instanceStatus(newStatus) {
      if (this.isStatusOpen(newStatus)) {
        this.qrCode = null;
        this.showQrModal = false;
      }
    },
    qrCode(newCode) {
      this.showQrModal = !!newCode;
    },
  },
  created() {
    this.loadSettings();
    this.checkInstanceStatus();
    this.loadInstanceSettings();
    this.loadWebhookConfig();
    emitter.on(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, this.onQrCodeUpdated);
    emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
  },

  unmounted() {
    emitter.off(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, this.onQrCodeUpdated);
    emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
  },
  methods: {
    isStatusOpen(status) {
      return ['open', 'connected'].includes((status || '').toLowerCase());
    },
    onQrCodeUpdated(payload) {
      if (payload.inbox_id !== this.inbox.id) return;

      // Normalize QR code to ensure it's a valid data URI
      let qrCode = payload.qr_code;
      if (qrCode && typeof qrCode === 'string' && !qrCode.startsWith('data:')) {
        qrCode = `data:image/png;base64,${qrCode}`;
      }

      this.qrCode = qrCode;
      this.showQrModal = !!qrCode;
    },

    async onConnectionUpdate(payload) {
      if (payload.inbox_id !== this.inbox.id) return;
      this.instanceStatus = payload.status;
      if (this.isStatusOpen(this.instanceStatus)) {
        await this.checkInstanceStatus();
        this.qrCode = null;
        this.showQrModal = false;
      }
    },

    loadSettings() {
      const config = this.inbox.provider_config || {};
      this.proxySettings = {
        enabled: config.proxy_enabled || false,
        host: config.proxy_host || '',
        port: config.proxy_port || '',
        username: config.proxy_username || '',
        password: config.proxy_password || '',
      };
    },

    async updateProxySettings() {
      if (!this.hasValidProxyConfig) {
        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.VALIDATION_ERROR'));
        return;
      }

      try {
        this.isLoading = true;

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              proxy_enabled: this.proxySettings.enabled,
              proxy_host: this.proxySettings.host,
              proxy_port: this.proxySettings.port,
              proxy_username: this.proxySettings.username,
              proxy_password: this.proxySettings.password,
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.SUCCESS'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.ERROR')
        );
      } finally {
        this.isLoading = false;
      }
    },

    async disconnectInstance() {
      try {
        this.isDisconnecting = true;

        await EvolutionAPI.logout(
          this.inbox.provider_config.api_url,
          this.inbox.provider_config.admin_token,
          this.inbox.provider_config.instance_name,
          this.inbox.provider_config.instance_token // For Evolution GO
        );

        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.SUCCESS'));
        this.instanceStatus = 'close';
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.ERROR')
        );
      } finally {
        this.isDisconnecting = false;
      }
    },

    async checkInstanceStatus() {
      try {
        const instances = await EvolutionAPI.fetchInstances(
          this.inbox.provider_config.api_url,
          this.inbox.provider_config.admin_token
        );
        const instance = instances.find(inst => {
          return (
            inst.name === this.inbox.provider_config.instance_name ||
            inst.instanceName === this.inbox.provider_config.instance_name
          );
        });

        if (instance) {
          // Evolution GO uses 'connected' (boolean), Evolution Node uses 'connectionStatus' or 'state'
          if (typeof instance.connected === 'boolean') {
            this.instanceStatus = instance.connected ? 'open' : 'close';
          } else {
            this.instanceStatus =
              instance.connectionStatus || instance.state || instance.status;
          }

          // Save instance_id (UUID) if not already saved
          // Evolution GO returns UUID in instance.id
          if (instance.id && !this.inbox.provider_config.instance_id) {
            this.saveInstanceId(instance.id);
          }

          // Clear QR Code when instance is connected
          if (this.isStatusOpen(this.instanceStatus)) {
            this.qrCode = null;
          }
        } else {
          this.instanceStatus = 'close';
        }
      } catch (error) {
        // Default to closed state if status cannot be fetched
        this.instanceStatus = 'close';
      }
    },

    async saveInstanceId(instanceId) {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              instance_id: instanceId,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
      } catch (error) {
        // Silent error - non-critical operation
      }
    },

    async generateQRCode() {
      try {
        this.isGeneratingQR = true;

        // Build webhook URL for Evolution GO
        // Format: {frontend_url}/webhooks/whatsapp/{phone_number}
        const phoneNumber =
          this.inbox.phone_number || this.inbox.provider_config.phone_number;
        const webhookUrl = phoneNumber
          ? `${window.location.origin}/webhooks/whatsapp/${phoneNumber.replace(/\+/g, '')}`
          : null;

        // getQRCode now returns normalized base64 data URI directly
        const qrCode = await EvolutionAPI.getQRCode(
          this.inbox.provider_config.api_url,
          this.inbox.provider_config.admin_token,
          this.inbox.provider_config.instance_name,
          this.inbox.provider_config.instance_token, // For Evolution GO
          webhookUrl // Webhook URL for Evolution GO
        );

        if (qrCode) {
          this.qrCode = qrCode;
          this.showQrModal = true;
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.SUCCESS'));
        } else {
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.NO_QR'));
        }
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.ERROR')
        );
      } finally {
        this.isGeneratingQR = false;
      }
    },

    async configureWebhook() {
      try {
        this.isConfiguringWebhook = true;

        // Check if this is Evolution GO (has instance_token)
        const instanceToken = this.inbox.provider_config.instance_token;
        if (!instanceToken) {
          useAlert('Webhook configuration is only available for Evolution GO');
          return;
        }

        // Build webhook URL
        const phoneNumber =
          this.inbox.phone_number || this.inbox.provider_config.phone_number;
        if (!phoneNumber) {
          useAlert('Phone number is required for webhook configuration');
          return;
        }

        const webhookUrl = `${window.location.origin}/webhooks/whatsapp/${phoneNumber.replace(/\+/g, '')}`;

        await EvolutionAPI.configureWebhook(
          this.inbox.provider_config.api_url,
          instanceToken,
          webhookUrl
        );

        useAlert(
          this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.SUCCESS') ||
            'Webhook configured successfully'
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.ERROR') ||
            'Failed to configure webhook'
        );
      } finally {
        this.isConfiguringWebhook = false;
      }
    },

    loadWebhookConfig() {
      // Set default webhook URL
      this.webhookConfig.webhookUrl = this.defaultWebhookUrl;

      // Load saved webhook config from provider_config if available
      const config = this.inbox.provider_config || {};
      if (config.webhook_events) {
        // Reset all events to false first
        Object.keys(this.webhookConfig.events).forEach(key => {
          this.webhookConfig.events[key] = false;
        });
        // Set saved events to true
        config.webhook_events.forEach(event => {
          if (
            Object.prototype.hasOwnProperty.call(
              this.webhookConfig.events,
              event
            )
          ) {
            this.webhookConfig.events[event] = true;
          }
        });
      }
      if (config.webhook_url) {
        this.webhookConfig.webhookUrl = config.webhook_url;
      }
      if (config.rabbitmq_enable) {
        this.webhookConfig.rabbitmqEnable = config.rabbitmq_enable;
      }
      if (config.websocket_enable) {
        this.webhookConfig.websocketEnable = config.websocket_enable;
      }
      if (config.nats_enable) {
        this.webhookConfig.natsEnable = config.nats_enable;
      }
    },

    selectAllEvents() {
      Object.keys(this.webhookConfig.events).forEach(key => {
        this.webhookConfig.events[key] = true;
      });
    },

    deselectAllEvents() {
      Object.keys(this.webhookConfig.events).forEach(key => {
        this.webhookConfig.events[key] = false;
      });
    },

    async saveWebhookConfig() {
      try {
        this.isSavingWebhookConfig = true;

        const instanceToken = this.inbox.provider_config.instance_token;
        if (!instanceToken) {
          useAlert(
            this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.EVOLUTION_GO_ONLY')
          );
          return;
        }

        // Get selected events
        const selectedEvents = Object.entries(this.webhookConfig.events)
          .filter(([, selected]) => selected)
          .map(([event]) => event);

        if (selectedEvents.length === 0) {
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.SELECT_EVENTS'));
          return;
        }

        // Save to Evolution GO via API
        await EvolutionAPI.configureWebhookFull(
          this.inbox.provider_config.api_url,
          instanceToken,
          this.webhookConfig.webhookUrl,
          selectedEvents,
          this.webhookConfig.rabbitmqEnable,
          this.webhookConfig.websocketEnable,
          this.webhookConfig.natsEnable
        );

        // Save config to Chatwoot inbox provider_config
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              webhook_url: this.webhookConfig.webhookUrl,
              webhook_events: selectedEvents,
              rabbitmq_enable: this.webhookConfig.rabbitmqEnable,
              websocket_enable: this.webhookConfig.websocketEnable,
              nats_enable: this.webhookConfig.natsEnable,
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.SUCCESS'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.ERROR')
        );
      } finally {
        this.isSavingWebhookConfig = false;
      }
    },

    async recreateInstance() {
      try {
        this.isRecreating = true;
        const response = await InboxesAPI.recreateEvolutionInstance(
          this.inbox.id
        );

        // Normalize QR code response
        const qrData = response.data?.qrcode || response.data;
        let qrCode = null;

        if (qrData) {
          if (typeof qrData === 'string') {
            qrCode = qrData.startsWith('data:')
              ? qrData
              : `data:image/png;base64,${qrData}`;
          } else {
            const rawQr =
              qrData.base64 || qrData.qrCode || qrData.code || qrData.qrcode;
            if (rawQr) {
              qrCode =
                typeof rawQr === 'string' && rawQr.startsWith('data:')
                  ? rawQr
                  : `data:image/png;base64,${rawQr}`;
            }
          }
        }

        if (qrCode) {
          this.qrCode = qrCode;
          this.showQrModal = true;
        }
        await this.checkInstanceStatus();
        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.SUCCESS'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.ERROR')
        );
      } finally {
        this.isRecreating = false;
      }
    },

    async refreshStatus() {
      await this.checkInstanceStatus();
      useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.STATUS.REFRESHED'));
    },

    async loadInstanceSettings() {
      try {
        const settings = await EvolutionAPI.findSettings(
          this.inbox.provider_config.api_url,
          this.inbox.provider_config.admin_token,
          this.inbox.provider_config.instance_name,
          this.inbox.provider_config.instance_token, // For Evolution GO
          this.inbox.provider_config.instance_id // UUID for Evolution GO
        );

        if (settings) {
          // Map settings from both Evolution GO and Evolution Node formats
          this.instanceSettings = {
            rejectCall: settings.rejectCall ?? false,
            msgCall: settings.msgCall || settings.msgRejectCall || '',
            // Evolution GO uses ignoreGroups, Evolution Node uses groupsIgnore
            groupsIgnore:
              settings.ignoreGroups ?? settings.groupsIgnore ?? false,
            alwaysOnline: settings.alwaysOnline ?? true,
            readMessages: settings.readMessages ?? false,
            syncFullHistory: settings.syncFullHistory ?? false,
            // Evolution GO uses ignoreStatus
            readStatus: settings.ignoreStatus ?? settings.readStatus ?? false,
          };
        }
      } catch (error) {
        // Silent error handling
      }
    },

    async updateInstanceSettings() {
      try {
        this.isLoading = true;

        // Build settings payload compatible with both Evolution GO and Evolution Node
        const settingsPayload = this.isEvolutionGO
          ? {
              // Evolution GO format
              alwaysOnline: this.instanceSettings.alwaysOnline,
              rejectCall: this.instanceSettings.rejectCall,
              msgRejectCall: this.instanceSettings.msgCall,
              readMessages: this.instanceSettings.readMessages,
              ignoreGroups: this.instanceSettings.groupsIgnore,
              ignoreStatus: this.instanceSettings.readStatus,
            }
          : {
              // Evolution Node format
              ...this.instanceSettings,
            };

        await EvolutionAPI.setSettings(
          this.inbox.provider_config.api_url,
          this.inbox.provider_config.admin_token,
          this.inbox.provider_config.instance_name,
          settingsPayload,
          this.inbox.provider_config.instance_token, // For Evolution GO
          this.inbox.provider_config.instance_id // UUID for Evolution GO
        );

        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.SUCCESS'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.ERROR')
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Instance Status and Management -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.EVOLUTION.MANAGEMENT.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.EVOLUTION.MANAGEMENT.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <InboxName
          :inbox="inbox"
          class="!text-lg !m-0"
          with-phone-number
          with-provider-connection-status
        />

        <div class="flex items-center gap-2">
          <span class="font-medium">{{
            $t('INBOX_MGMT.ADD.EVOLUTION.STATUS.LABEL')
          }}</span>
          <span
            :class="{
              'text-green-600': isStatusOpen(instanceStatus),
              'text-yellow-600': instanceStatus === 'connecting',
              'text-red-600': instanceStatus === 'close',
              'text-gray-600': !instanceStatus,
            }"
            class="font-semibold capitalize"
          >
            {{ instanceStatus || 'Unknown' }}
          </span>
          <NextButton
            size="small"
            variant="link"
            :label="$t('INBOX_MGMT.ADD.EVOLUTION.STATUS.REFRESH')"
            @click="refreshStatus"
          />
        </div>

        <div class="flex gap-2">
          <NextButton
            v-if="canShowQRCode"
            :label="$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.GENERATE')"
            :is-loading="isGeneratingQR"
            @click="generateQRCode"
          />

          <NextButton
            :label="$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.BUTTON')"
            :is-loading="isRecreating"
            @click="recreateInstance"
          />

          <NextButton
            v-if="inbox.provider_config.instance_token"
            variant="secondary"
            :label="$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK.BUTTON')"
            :is-loading="isConfiguringWebhook"
            @click="configureWebhook"
          />

          <NextButton
            variant="warning"
            :label="$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.BUTTON')"
            :is-loading="isDisconnecting"
            @click="disconnectInstance"
          />
        </div>

        <Modal
          v-if="qrCode"
          v-model:show="showQrModal"
          :on-close="() => (showQrModal = false)"
        >
          <woot-modal-header
            :header-title="$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.TITLE')"
          />
          <div class="p-8 text-center">
            <img
              :src="qrCode"
              alt="WhatsApp QR Code"
              class="max-w-xs mx-auto block"
            />
            <p class="text-sm text-gray-600 mt-2">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.INSTRUCTIONS') }}
            </p>
          </div>
        </Modal>
      </div>
    </SettingsSection>

    <!-- Proxy Settings -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.SUBTITLE')"
    >
      <div class="space-y-4">
        <!-- Enable Proxy Checkbox -->
        <div class="flex items-center gap-2">
          <input
            id="proxyEnabled"
            v-model="proxySettings.enabled"
            type="checkbox"
          />
          <label for="proxyEnabled" class="font-medium">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.ENABLE') }}
          </label>
        </div>

        <!-- Proxy Configuration (shown when enabled) -->
        <div v-if="proxySettings.enabled" class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.HOST') }}
            </label>
            <input
              v-model="proxySettings.host"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.HOST_PLACEHOLDER')
              "
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.PORT') }}
            </label>
            <input
              v-model="proxySettings.port"
              type="number"
              :placeholder="
                $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.PORT_PLACEHOLDER')
              "
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.USERNAME') }}
            </label>
            <input
              v-model="proxySettings.username"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.USERNAME_PLACEHOLDER')
              "
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.PASSWORD') }}
            </label>
            <input
              v-model="proxySettings.password"
              type="password"
              :placeholder="
                $t('INBOX_MGMT.ADD.EVOLUTION.PROXY.PASSWORD_PLACEHOLDER')
              "
              class="w-full"
            />
          </div>
        </div>

        <NextButton
          :label="$t('INBOX_MGMT.ADD.EVOLUTION.PROXY.UPDATE')"
          :is-loading="isLoading"
          :disabled="!hasValidProxyConfig"
          @click="updateProxySettings"
        />
      </div>
    </SettingsSection>

    <!-- Webhook Configuration (Evolution GO only) -->
    <SettingsSection
      v-if="isEvolutionGO"
      :title="$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.SUBTITLE')"
    >
      <div class="space-y-6">
        <!-- Webhook URL -->
        <div>
          <label class="block text-sm font-medium mb-1">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.URL_LABEL') }}
          </label>
          <input
            v-model="webhookConfig.webhookUrl"
            type="url"
            :placeholder="defaultWebhookUrl"
            class="w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.URL_HELP') }}
          </p>
        </div>

        <!-- Webhook Events -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h4 class="font-medium text-gray-700">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.EVENTS_TITLE') }}
            </h4>
            <div class="flex gap-2">
              <button
                type="button"
                class="text-xs text-blue-600 hover:text-blue-800"
                @click="selectAllEvents"
              >
                {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.SELECT_ALL') }}
              </button>
              <span class="text-gray-400">&bull;</span>
              <button
                type="button"
                class="text-xs text-blue-600 hover:text-blue-800"
                @click="deselectAllEvents"
              >
                {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.DESELECT_ALL') }}
              </button>
            </div>
          </div>

          <p class="text-xs text-gray-500">
            {{
              $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.EVENTS_SELECTED', {
                count: selectedEventsCount,
              })
            }}
          </p>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div
              v-for="event in availableEvents"
              :key="event.key"
              class="flex items-start gap-2 p-2 rounded border border-gray-200 hover:border-blue-300 transition-colors"
              :class="{
                'bg-blue-50 border-blue-300': webhookConfig.events[event.key],
              }"
            >
              <input
                :id="'event-' + event.key"
                v-model="webhookConfig.events[event.key]"
                type="checkbox"
                class="mt-1"
              />
              <label :for="'event-' + event.key" class="cursor-pointer flex-1">
                <span class="block text-sm font-medium">{{ event.label }}</span>
                <span class="block text-xs text-gray-500">{{
                  event.description
                }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Messaging Options -->
        <div class="space-y-4">
          <h4 class="font-medium text-gray-700">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.MESSAGING_TITLE') }}
          </h4>

          <div class="grid grid-cols-3 gap-4">
            <!-- RabbitMQ -->
            <div>
              <label class="block text-sm font-medium mb-1">
                {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.RABBITMQ') }}
              </label>
              <select v-model="webhookConfig.rabbitmqEnable" class="w-full">
                <option
                  v-for="option in messagingOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- WebSocket -->
            <div>
              <label class="block text-sm font-medium mb-1">
                {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.WEBSOCKET') }}
              </label>
              <select v-model="webhookConfig.websocketEnable" class="w-full">
                <option
                  v-for="option in messagingOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- NATS -->
            <div>
              <label class="block text-sm font-medium mb-1">
                {{ $t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.NATS') }}
              </label>
              <select v-model="webhookConfig.natsEnable" class="w-full">
                <option
                  v-for="option in messagingOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <NextButton
          :label="$t('INBOX_MGMT.ADD.EVOLUTION.WEBHOOK_CONFIG.SAVE')"
          :is-loading="isSavingWebhookConfig"
          @click="saveWebhookConfig"
        />
      </div>
    </SettingsSection>

    <!-- Advanced Settings -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.SUBTITLE')"
    >
      <div class="space-y-4">
        <!-- Call Settings -->
        <div class="space-y-3">
          <h4 class="font-medium text-gray-700">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.CALLS.TITLE') }}
          </h4>

          <div class="flex items-center gap-2">
            <input
              id="rejectCall"
              v-model="instanceSettings.rejectCall"
              type="checkbox"
            />
            <label for="rejectCall" class="text-sm">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.CALLS.REJECT') }}
            </label>
          </div>

          <div v-if="instanceSettings.rejectCall">
            <label class="block text-sm font-medium mb-1">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.CALLS.MESSAGE') }}
            </label>
            <input
              v-model="instanceSettings.msgCall"
              type="text"
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.EVOLUTION.SETTINGS.CALLS.MESSAGE_PLACEHOLDER'
                )
              "
              class="w-full"
            />
          </div>
        </div>

        <!-- Group Settings -->
        <div class="space-y-3">
          <h4 class="font-medium text-gray-700">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.GROUPS.TITLE') }}
          </h4>

          <div class="flex items-center gap-2">
            <input
              id="groupsIgnore"
              v-model="instanceSettings.groupsIgnore"
              type="checkbox"
            />
            <label for="groupsIgnore" class="text-sm">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.GROUPS.IGNORE') }}
            </label>
          </div>
        </div>

        <!-- Status Settings -->
        <div class="space-y-3">
          <h4 class="font-medium text-gray-700">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.STATUS.TITLE') }}
          </h4>

          <div class="flex items-center gap-2">
            <input
              id="alwaysOnline"
              v-model="instanceSettings.alwaysOnline"
              type="checkbox"
            />
            <label for="alwaysOnline" class="text-sm">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.STATUS.ALWAYS_ONLINE') }}
            </label>
          </div>
        </div>

        <!-- Message Settings -->
        <div class="space-y-3">
          <h4 class="font-medium text-gray-700">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.MESSAGES.TITLE') }}
          </h4>

          <div class="flex items-center gap-2">
            <input
              id="readMessages"
              v-model="instanceSettings.readMessages"
              type="checkbox"
            />
            <label for="readMessages" class="text-sm">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.MESSAGES.READ') }}
            </label>
          </div>

          <div class="flex items-center gap-2">
            <input
              id="readStatus"
              v-model="instanceSettings.readStatus"
              type="checkbox"
            />
            <label for="readStatus" class="text-sm">
              {{ $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.MESSAGES.READ_STATUS') }}
            </label>
          </div>

          <div class="flex items-center gap-2">
            <input
              id="syncFullHistory"
              v-model="instanceSettings.syncFullHistory"
              type="checkbox"
            />
            <label for="syncFullHistory" class="text-sm">
              {{
                $t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.MESSAGES.SYNC_HISTORY')
              }}
            </label>
          </div>
        </div>

        <NextButton
          :label="$t('INBOX_MGMT.ADD.EVOLUTION.SETTINGS.UPDATE')"
          :is-loading="isLoading"
          @click="updateInstanceSettings"
        />
      </div>
    </SettingsSection>
  </div>
</template>
